import type { Stack } from "../../shared/stack.js";
import { h, opaque } from "../dom.js";
import { amount, compact, time } from "../format.js";

const SVG = "http://www.w3.org/2000/svg";
const W = 720;
const HEIGHT = 260;
const PAD = { left: 52, right: 14, top: 12, bottom: 26 };

function s<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>, text?: string): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  if (text !== undefined) el.textContent = text;
  return el;
}

/** Round tick values from 0 to at least `max`. */
function ticks(max: number, count = 4): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((v) => v >= raw)!;
  const out: number[] = [];
  for (let v = 0; v < max + step * 0.999; v += step) out.push(v);
  return out;
}

function clock(at: number, spanMs: number): string {
  const d = new Date(at);
  if (spanMs > 2 * 86_400_000) return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export interface ChartInput {
  stack: Stack;
  /** [time, amount], oldest first; each amount holds until the next point. */
  points: Array<[number, number]>;
  from: number;
  to: number;
  min: number;
  target: number;
  /** Names of the two reference lines (default "min" and "target"). */
  refLabels?: [string, string];
  /** How values read in the tooltip, the axis and the reference labels (default: the stack's amount). */
  format?: (v: number) => string;
  /** Shown instead of the chart when there are no points. */
  emptyText?: string;
  /** Top of the value axis, when it is fixed (a percentage). */
  yMax?: number;
  /** Height of the drawing against its 720 width (default 260); lower draws a flatter chart. */
  height?: number;
}

/**
 * Stock over time as a step line, with the rule's min and target as reference
 * lines and a crosshair that reads the value under the pointer.
 */
export function historyChart(input: ChartInput): HTMLElement {
  const { points, from, to, min, target, stack } = input;
  const H = input.height ?? HEIGHT;
  const fmt = input.format ?? ((v: number) => amount(v, stack.type));
  const [minLabel, targetLabel] = input.refLabels ?? ["min", "target"];
  // Its listeners hold the elements built here, so a patch replaces the chart whole.
  const wrap = opaque(h("div", { class: "chart" }));
  if (points.length === 0) {
    wrap.append(h("p", { class: "muted empty" }, input.emptyText ?? "No history yet. The app samples stock once a minute while the game is connected."));
    return wrap;
  }
  const yTicks = ticks(input.yMax ?? Math.max(target, min, ...points.map((p) => p[1])) * 1.05);
  const yMax = yTicks[yTicks.length - 1];
  const span = Math.max(1, to - from);
  const x = (t: number) => PAD.left + ((t - from) / span) * (W - PAD.left - PAD.right);
  const y = (v: number) => H - PAD.bottom - (v / yMax) * (H - PAD.top - PAD.bottom);

  const svg = s("svg", { viewBox: `0 0 ${W} ${H}`, role: "img", "aria-label": `${stack.label ?? stack.name} over time` });
  for (const v of yTicks) {
    svg.append(s("line", { class: "grid", x1: PAD.left, x2: W - PAD.right, y1: y(v), y2: y(v) }));
    svg.append(s("text", { class: "axis", x: PAD.left - 6, y: y(v) + 4, "text-anchor": "end" }, input.format ? fmt(v) : compact(v)));
  }
  for (let i = 0; i <= 4; i++) {
    const t = from + (span * i) / 4;
    svg.append(s("text", { class: "axis", x: x(t), y: H - 8, "text-anchor": i === 0 ? "start" : i === 4 ? "end" : "middle" }, clock(t, span)));
  }

  // Step line: hold each amount until the next point.
  let d = `M${x(Math.max(from, points[0][0]))},${y(points[0][1])}`;
  for (let i = 1; i < points.length; i++) d += `H${x(points[i][0])}V${y(points[i][1])}`;
  d += `H${x(to)}`;
  const area = `${d}V${y(0)}H${x(Math.max(from, points[0][0]))}Z`;
  svg.append(s("path", { class: "area", d: area }));

  for (const [value, cls, label] of [[min, "min", minLabel], [target, "target", targetLabel]] as const) {
    if (value <= 0) continue;
    svg.append(s("line", { class: `ref ${cls}`, x1: PAD.left, x2: W - PAD.right, y1: y(value), y2: y(value) }));
    svg.append(s("text", { class: "ref-label", x: W - PAD.right - 4, y: y(value) - 4, "text-anchor": "end" }, `${label} ${input.format ? fmt(value) : compact(value)}`));
  }
  svg.append(s("path", { class: "line", d }));

  const cross = s("line", { class: "cross", x1: 0, x2: 0, y1: PAD.top, y2: H - PAD.bottom, visibility: "hidden" });
  const dot = s("circle", { class: "dot", r: 4, cx: 0, cy: 0, visibility: "hidden" });
  const hit = s("rect", { class: "hit", x: PAD.left, y: 0, width: W - PAD.left - PAD.right, height: H });
  svg.append(cross, dot, hit);
  const tip = h("div", { class: "chart-tip", hidden: true });
  wrap.append(svg, tip);

  const valueAt = (t: number): number => {
    let v = points[0][1];
    for (const [at, amt] of points) {
      if (at > t) break;
      v = amt;
    }
    return v;
  };
  hit.addEventListener("pointermove", (ev) => {
    const box = svg.getBoundingClientRect();
    const px = ((ev.clientX - box.left) / box.width) * W;
    const t = from + ((Math.min(Math.max(px, PAD.left), W - PAD.right) - PAD.left) / (W - PAD.left - PAD.right)) * span;
    const v = valueAt(t);
    for (const el of [cross, dot]) el.setAttribute("visibility", "visible");
    cross.setAttribute("x1", String(x(t)));
    cross.setAttribute("x2", String(x(t)));
    dot.setAttribute("cx", String(x(t)));
    dot.setAttribute("cy", String(y(v)));
    tip.hidden = false;
    tip.replaceChildren(h("strong", null, fmt(v)), h("span", { class: "muted" }, time(t)));
    const left = (x(t) / W) * box.width;
    tip.style.left = `${Math.min(left + 12, box.width - 150)}px`;
    tip.style.top = `${(y(v) / H) * box.height - 40}px`;
  });
  hit.addEventListener("pointerleave", () => {
    for (const el of [cross, dot]) el.setAttribute("visibility", "hidden");
    tip.hidden = true;
  });
  return wrap;
}
